import { HistoricalPaymentDetail, ScheduledPaymentDetail } from "../../common/models/memberclaims";

export const formatAsCurrency = (amount: number, showSymbol: boolean = true): string => {
  const sign = amount < 0 ? "-" : "";
  const abs = Math.abs(amount);
  const formatted = abs.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,");
  return `${sign}${showSymbol ? "$" : ""}${formatted}`;
};

export const formatDate = (dateString: string, fullMonth = false): string => {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = { 
    year: 'numeric',
    month: fullMonth ? 'long' : 'short',
    day: 'numeric'
  };
  return date.toLocaleDateString('en-GB', options);
};

export const distinctByBenefitGroupId = (payments: HistoricalPaymentDetail[]): HistoricalPaymentDetail[] => {
  const map = new Map<string, HistoricalPaymentDetail>();
  for (const p of payments) {
    map.set(p.benefitGroupId, p); // overwrites previous if exists
  }
  return Array.from(map.values());
}

/**
 * Returns all PaymentDetails that are in the same group as the item with `paymentId`.
 * Grouping is by (claimCaseNumber, benefitCaseNumber, messageID, dateFrom, dateTo).
 * - If the paymentId isn't found, returns [].
 * - Items are returned as-is (original references), in the same order as provided.
 */
export const getGroupMembersByPaymentId = (
  paymentId: number,
  payments: HistoricalPaymentDetail[]
): HistoricalPaymentDetail[] => {
  const target = payments.find(p => p.paymentId === paymentId);
  if (!target) return [];

  return payments.filter(p => p.benefitGroupId === target.benefitGroupId);
}

// Overloads
export function getPaginatedPayments(
  payments: HistoricalPaymentDetail[],
  pageNumber: number,
  paymentsPerPage: number
): HistoricalPaymentDetail[];

export function getPaginatedPayments(
  payments: ScheduledPaymentDetail[],
  pageNumber: number,
  paymentsPerPage: number
): ScheduledPaymentDetail[];

// Impl
export function getPaginatedPayments<T extends HistoricalPaymentDetail | ScheduledPaymentDetail>(
  payments: T[],
  pageNumber: number,
  paymentsPerPage: number
): T[] {
  const startIndex = (pageNumber - 1) * paymentsPerPage;
  const endIndex = startIndex + paymentsPerPage;
  return payments.slice(startIndex, endIndex);
}


export const getNextScheduledPayment = (
  claimId: number,
  scheduledPayments: ScheduledPaymentDetail[] | undefined
): ScheduledPaymentDetail | undefined => {
  if (!scheduledPayments) return undefined;

  let claimPayments = scheduledPayments
    .filter(sp => sp.claimId === claimId)
    .sort((a, b) => new Date(a.paymentDate).getTime() - new Date(b.paymentDate).getTime())

  return claimPayments.length > 0 ? claimPayments[0] : undefined;
};

export const getSortedScheduledPayments = (
  scheduledPayments: ScheduledPaymentDetail[] | undefined
): ScheduledPaymentDetail[] => (scheduledPayments || [])
  .sort((a, b) => new Date(a.paymentDate).getTime() - new Date(b.paymentDate).getTime());
